import { TabsProps } from '@mui/material/Tabs/Tabs';
import { useMemo } from 'react';

import { theme } from '~/theme';
import { cssColorHexToRgbA, getInsetEdgeShadows } from '~/utils/helper';

export type SolaForceTabsStyleProps = Pick<TabsProps, 'orientation'> & {
  background?: 'default' | 'transparent' | 'fade';
  shadow?: 'default' | 'inset' | 'bottom' | 'none';
};

export const useSxStyles = (props: SolaForceTabsStyleProps) => {
  return useMemo(() => {
    const { background, shadow, orientation } = props;
    const isVertical = orientation === 'vertical';

    const getBackgroundConfig = (bg: SolaForceTabsStyleProps['background']) => {
      const bgColor = '#eee';

      switch (bg) {
        case 'fade':
          const bgGradient = `linear-gradient(${
            isVertical ? '90deg' : '180deg'
          }, ${cssColorHexToRgbA(bgColor, 0)}, ${cssColorHexToRgbA(
            bgColor,
            0,
          )} 5%, ${cssColorHexToRgbA(bgColor, 1)} 50%, ${cssColorHexToRgbA(
            bgColor,
            1,
          )})`;

          return {
            backgroundColor: 'transparent',
            backgroundImage: bgGradient,
          };
        case 'transparent':
          return {
            backgroundColor: 'transparent',
          };
        default:
          return {
            backgroundColor: bgColor,
          };
      }
    };

    const getShadowConfig = (s: SolaForceTabsStyleProps['shadow']) => {
      switch (s) {
        case 'none':
          return {};
        case 'inset':
          return {
            boxShadow:
              '0px 2px 1px -1px rgba(0,0,0,0.2) inset,' +
              '0px 1px 1px 0px rgba(0,0,0,0.14) inset,' +
              '0px 1px 3px 0px rgba(0,0,0,0.12) inset',
          };
        case 'bottom':
        default:
          return {
            boxShadow: getInsetEdgeShadows({
              edges: [isVertical ? 'right' : 'bottom'],
            }),
          };
      }
    };

    return {
      tabs: {
        ...getBackgroundConfig(background),
        ...getShadowConfig(shadow),
        '& .MuiTabs-flexContainer': {
          position: 'relative',
          zIndex: 10,
        },
        '& .MuiTabScrollButton-root.Mui-disabled': {
          opacity: 0.15,
        },
        '& .MuiTab-root': {
          '&.summary': {
            '&:before': {
              content: `""`,
              display: 'block',
              width: '100%',
              height: '3px',
              marginTop: '-3px',
              backgroundColor: theme.palette.divider,
              position: 'relative',
              top: theme.spacing(-1),
              borderRadius: '2px',
            },

            '&:hover': {
              '&:before': {
                backgroundColor: theme.palette.primary.main,
              },
            },

            '&.Mui-selected': {
              '&:before': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
        },
      },
      tabIndicator: {
        backgroundColor: '#fff',
        zIndex: 1,
        boxShadow: theme.shadows[1],
        ...(isVertical
          ? {
              width: 'calc(100% - 4px)',
              borderLeft: `solid 3px ${theme.palette.primary.main}`,
              borderRadius: theme.spacing(0.5, 0, 0, 0.5),
            }
          : {
              height: 'calc(100% - 4px)',
              borderTop: `solid 3px ${theme.palette.primary.main}`,
              borderRadius: theme.spacing(0.5, 0.5, 0, 0),
            }),
      },
    };
  }, [props]);
};
